<template>
  <AtlasDialog
    v-model="showDialog"
    eyebrow="INFO"
    :title="t('licenseAgreement.title').value"
    max-width="900"
    :persistent="true"
    @close="showDialog = false"
  >
    <div class="pt-4">
      <p class="text-body-1 mb-4">
        {{
          t('licenseAgreement.description')
        }}
      </p>

      <div class="license-content">
        <!-- SNOMED License Section -->
        <div class="license-section mb-6">
          <h2 class="text-h6 mb-3">
            SNOMED INTERNATIONAL SNOMED CT LICENSE AGREEMENT
          </h2>
          <div class="license-text">
            <p>
              <strong>SNOMED International SNOMED CT Browser</strong> includes SNOMED Clinical
              Terms® (SNOMED CT®) which is used by permission of the SNOMED International. All
              rights reserved. SNOMED CT® was originally created by the College of American
              Pathologists. "SNOMED", "SNOMED CT" and "SNOMED Clinical Terms" are registered
              trademarks of the SNOMED International (<a
                href="http://www.snomed.org"
                target="_blank"
                rel="noopener noreferrer"
              >www.snomed.org</a>)
            </p>
            <p>
              Use of SNOMED CT in <strong>SNOMED International SNOMED CT Browser</strong> is
              governed by the conditions of the following SNOMED CT license issued by SNOMED
              International:
            </p>
            <ol>
              <li>
                The meaning of the terms "Affiliate", or "Data Analysis System", "Data Creation
                System", "Derivative", "End User", "Extension", "Member", "Non-Member Territory",
                "SNOMED CT" and "SNOMED CT Content" are as defined in the SNOMED International
                Affiliate License Agreement (see
                <a
                  href="http://www.snomed.org/resource/resource/117"
                  target="_blank"
                  rel="noopener noreferrer"
                >on the SNOMED International website</a>).
              </li>
              <li>
                Information about Affiliate Licensing is available at
                <a
                  href="http://www.snomed.org/snomed-ct/get-snomed-ct"
                  target="_blank"
                  rel="noopener noreferrer"
                >http://www.snomed.org/snomed-ct/get-snomed-ct</a>. Individuals or organizations wishing to register as SNOMED International
                Affiliates can register at
                <a
                  href="https://mlds.ihtsdotools.org"
                  target="_blank"
                  rel="noopener noreferrer"
                >mlds.ihtsdotools.org</a>, subject to acceptance of the Affiliate License Agreement (see
                <a
                  href="http://www.snomed.org/resource/resource/117"
                  target="_blank"
                  rel="noopener noreferrer"
                >on the SNOMED International website)</a>.
              </li>
              <li>
                The current list of SNOMED International Member Territories can be viewed at
                <a
                  href="http://www.snomed.org/members"
                  target="_blank"
                  rel="noopener noreferrer"
                >www.snomed.org/members</a>. Countries not included in that list are "Non-Member Territories".
              </li>
              <li>
                End Users, that do not hold the SNOMED International Affiliate License, may access
                SNOMED CT® using
                <strong>SNOMED International SNOMED CT Browser</strong> subject to acceptance of
                and adherence to the following sub-license limitations:

                <ol type="a">
                  <li>
                    The sub-licensee is only permitted to access SNOMED CT® using this software
                    (or service) for the purpose of exploring and evaluating the terminology.
                  </li>
                  <li>
                    The sub-licensee is not permitted the use of this software as part of a system
                    that constitutes a SNOMED CT "Data Creation System" or "Data Analysis System",
                    as defined in the SNOMED International Affiliate License. This means that the
                    sub-licensee must not use
                    <strong>SNOMED International SNOMED CT Browser</strong> to add or copy SNOMED
                    CT identifiers into any type of record system, database or document.
                  </li>
                  <li>
                    The sub-licensee is not permitted to translate or modify SNOMED CT Content or
                    Derivatives.
                  </li>
                  <li>
                    The sub-licensee is not permitted to distribute or share SNOMED CT Content or
                    Derivatives.
                  </li>
                </ol>
              </li>
              <li>
                SNOMED International Affiliates may use
                <strong>SNOMED International SNOMED CT Browser</strong> as part of a "Data
                Creation System" or "Data Analysis System" subject to the following conditions:

                <ol type="a">
                  <li>
                    The SNOMED International Affiliate, using
                    <strong>SNOMED International SNOMED CT Browser</strong> must accept full
                    responsibility for any reporting and fees due for use or deployment of such a
                    system in a Non-Member Territory.
                  </li>
                  <li>
                    The SNOMED International Affiliate must not use
                    <strong>SNOMED International SNOMED CT Browser</strong> to access or interact
                    with SNOMED CT in any way that is not permitted by the Affiliate License
                    Agreement.
                  </li>
                  <li>
                    In the event of termination of the Affiliate License Agreement, the use of
                    <strong>SNOMED International SNOMED CT Browser</strong> will be subject to the
                    End User limitations noted in 4.
                  </li>
                </ol>
              </li>
            </ol>
          </div>
        </div>

        <!-- HemOnc License Section -->
        <div class="license-section">
          <h2 class="text-h6 mb-3">
            HemOnc license agreement
          </h2>
          <div class="license-text">
            <p>
              The HemOnc Vocabulary is a derivative product of HemOnc.org, which is wholly owned
              and operated by HemOnc.org LLC. Use of the HemOnc Vocabulary is governed by the
              conditions of this License Agreement issued by HemOnc.org LLC:
            </p>
            <ol>
              <li>
                The meaning of the terms "Data Analysis System", "Data Creation System", and
                "License" is defined below.
              </li>
              <li>
                If you are already holding a HemOnc ontology License, you are subject to the terms
                and conditions of the License for the version of the HemOnc Vocabulary which was
                provided to you. If you wish to obtain this version of the HemOnc Vocabulary, you
                are subject to the following limitations.
              </li>
              <li>
                If you currently do not hold a HemOnc ontology License, you may access the HemOnc
                Vocabulary subject to acceptance of and adherence to the following limitations:

                <ol type="a">
                  <li>
                    You are permitted to access HemOnc using this software for the purpose of
                    exploring and evaluating the terminology.
                  </li>
                  <li>
                    You are permitted to use HemOnc as part of a system that constitutes a Data
                    Analysis System, as defined below.
                  </li>
                  <li>
                    You are not permitted the use of HemOnc as part of a system that constitutes a
                    Data Creation System, as defined below. This means that you must not use
                    HemOnc identifiers in any type of system intended for direct or indirect
                    patient care.
                  </li>
                  <li>
                    If you share the Licensed Material with a third party, attribution of the
                    HemOnc vocabulary must be given to HemOnc.org LLC, with a URI or hyperlink to
                    HemOnc.org provided if reasonably possible. Use of HemOnc by a third party
                    would be limited to a Data Analysis System and a copy of this License
                    Agreement must be supplied to the third party.
                  </li>
                  <li>
                    You may not use HemOnc as a substitute for formal medical training, substitute
                    it for independent clinical judgment, and construe it as offering medical
                    advice.
                  </li>
                </ol>
              </li>
            </ol>

            <p>
              If you wish to utilize the vocabulary for purposes other than specified here,
              contact us by emailing
              <a href="mailto:licensing@hemonc.org">licensing@hemonc.org</a>.
            </p>
            <p>
              You agree to indemnify and hold HemOnc.org LLC and all parties who have contributed
              to HemOnc.org and this derivative product harmless from and against all claims,
              liability, losses, damages, costs, and expenses, including--but not limited
              to--legal fees
            </p>

            <p class="font-weight-bold mt-4">
              Definitions
            </p>
            <p>
              Data Analysis System: a computer system that is used to analyze records or other
              data, but not if that system is also a Data Creation System
            </p>
            <p>
              Data Creation System: a computer system that is used to create records or other data
              that is encoded using HemOnc, for the purposes of facilitating patient care
            </p>
            <p>
              License: the separate
              <a
                href="https://creativecommons.org/licenses/by-nc-sa/4.0/"
                target="_blank"
                rel="noopener noreferrer"
              >CC BY-NC-SA 4.0</a>
              license to the full HemOnc ontology
            </p>
          </div>
        </div>
      </div>
    </div>
    <template #actions>
      <AtlasButton
        variant="ghost"
        @click="handleReject"
      >
        {{ t('licenseAgreement.reject') }}
      </AtlasButton>
      <AtlasButton
        @click="handleAccept"
      >
        {{ t('licenseAgreement.accept') }}
      </AtlasButton>
    </template>
  </AtlasDialog>
</template>

<script setup lang="ts">
import { AtlasButton, AtlasDialog } from '@/components/ui'
import { computed } from 'vue'
import { useI18n } from '@/composables/useI18n'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  accept: []
  reject: []
}>()

const { t } = useI18n()

const showDialog = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

function handleAccept() {
  emit('accept')
  emit('update:modelValue', false)
}

function handleReject() {
  emit('reject')
}
</script>

<style scoped>
.license-content {
  max-height: 500px;
  overflow-y: auto;
}

.license-section {
  padding: 16px;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 4px;
}

.license-text {
  font-size: 0.875rem;
  line-height: 1.5;
}

.license-text p {
  margin-bottom: 12px;
}

.license-text ol {
  padding-left: 20px;
  margin-bottom: 12px;
}

.license-text ol ol {
  list-style-type: lower-alpha;
  margin-top: 8px;
}

.license-text li {
  margin-bottom: 8px;
}

.license-text a {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.license-text a:hover {
  text-decoration: underline;
}
</style>
